import React, { useEffect, useState } from 'react';

import { Alert, Modal as ModalAntD, Spin } from 'antd';
import { T } from 'react-translator-component';

import { getDepositsTotalizers } from '../../../hooks/users';

/**
 * Returns the translated string for a key that contains a {value} placeholder,
 * replacing it with the provided value. TF uses {0}-style positional args, so
 * we manually replace after translating to keep locale keys consistent.
 */
const Tv = (key: string, value: string): string => {
  const translated = String((T(key) as any[])[0] ?? key);
  return translated.replace('{value}', value);
};

const Modal: any = ModalAntD;

interface Props {
  transaction: any;
  visible: boolean;
  onCancel: () => void;
  onConfirm: (opts: {
    cancelExistingPreAuth: boolean;
  }) => Promise<void>;
}

export const WalletChargeConfirmationModal: React.FC<Props> = ({
  transaction,
  visible,
  onCancel,
  onConfirm,
}) => {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible || !transaction) return;
    setLoading(true);
    setBalance(null);
    getDepositsTotalizers(transaction.userPk)
      .then((res: any) => setBalance(Number(res?.total ?? 0)))
      .catch(() => setBalance(0))
      .finally(() => setLoading(false));
  }, [visible, transaction]);

  const income = Number(transaction?.income ?? 0);
  const willGoNegative = balance !== null && balance - income < 0;
  const negativeAmount = balance !== null ? Math.max(0, income - balance) : 0;
  const hasOpenPreAuth =
    transaction?.paymentType === 'PAGBANK_CARD' &&
    !transaction?.isPaid &&
    transaction?.paymentExternalId != null;

  const handleOk = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        cancelExistingPreAuth: hasOpenPreAuth,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Bloqueia o botao quando o saldo pos-cobranca ficaria negativo — o backend rejeita
  // esse mesmo cenario com 400; o disable aqui evita o roundtrip e deixa a razao explicita.
  const okDisabled = loading || balance === null || willGoNegative;

  return (
    <Modal
      title={T('Charge wallet title')}
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={submitting}
      okText={T('Charge wallet')}
      okButtonProps={{ disabled: okDisabled }}
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <p>
            {T('Balance')}: R$ {(balance ?? 0).toFixed(2)}
            <br />
            {T('Amount')}: R$ {income.toFixed(2)}
          </p>
          {willGoNegative && (
            <Alert
              type='error'
              showIcon
              style={{ marginTop: 8 }}
              message={Tv('Wallet balance insufficient', negativeAmount.toFixed(2))}
            />
          )}
          {hasOpenPreAuth && (
            <Alert
              type='warning'
              showIcon
              style={{ marginTop: 8 }}
              message={T('Wallet with open pre-auth')}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default WalletChargeConfirmationModal;
